import { memo } from 'react';
import { MTableBody } from '@material-table/core';
import type { ListRowProps } from 'react-virtualized';
import { S2 } from '../../../../theme';

/*
  Original MTableBody's 'renderUngroupedRows' method implementation is taken from:
  https://github.com/material-table-core/core/blob/v0.2.37/src/components/m-table-body.js
  The first part is in the 'TableBody' class.
*/

const getBackgroundColor = (index: ListRowProps['index']): string | undefined => (
  index % 2 === 0 ? S2 : undefined
);

const TableRowRenderer = (props: any) => {
  const {
    // MTableBody props
    onToggleDetailPanel,
    onBulkEditRowChanged,
    renderData,
    onCellEditStarted,
    hasAnyEditingRow,
    isTreeData,
    onRowSelected,
    errorState,
    options,
    components,
    onTreeExpandChanged,
    bulkEditChangedRows,
    pageSize,
    actions,
    currentPage,
    detailPanel,
    onRowClick,
    icons,
    cellEditable,
    onEditingCanceled,
    onEditingApproved,
    treeDataMaxLevel,
    localization,
    onCellEditFinished,
    columns,
    getFieldValue,
    scrollWidth,
    onRowDoubleClick,
    bulkEditOpen,

    // virtualized list props
    index,
  } = props;

  const data = renderData[index];

  const sx = { backgroundColor: getBackgroundColor(index) };

  if (data.tableData.editing || bulkEditOpen) {
    return (
      <props.components.EditRow
        // @ts-ignore
        columns={columns.filter((columnDef) => !columnDef.hidden)}
        components={components}
        data={data}
        errorState={errorState}
        icons={icons}
        localization={{
          // @ts-ignore
          ...MTableBody.defaultProps.localization.editRow,
          ...localization.editRow,
          dateTimePickerLocalization: localization.dateTimePickerLocalization,
        }}
        key={`row-${data.tableData.uuid}`}
        mode={bulkEditOpen ? 'bulk' : data.tableData.editing}
        options={options}
        isTreeData={isTreeData}
        detailPanel={detailPanel}
        onEditingCanceled={onEditingCanceled}
        onEditingApproved={onEditingApproved}
        bulkEditChangedRows={bulkEditChangedRows}
        getFieldValue={getFieldValue}
        onBulkEditRowChanged={onBulkEditRowChanged}
        scrollWidth={scrollWidth}
        // custom props
        sx={sx}
      />
    );
  }
  // Treeified data is using the uuid, while the grouped data used the index
  const path = isTreeData ? [data.tableData.uuid] : [index + pageSize * currentPage];
  return (
    <props.components.Row
      components={components}
      icons={icons}
      data={data}
      index={index}
      errorState={errorState}
      key={`row-${data.tableData.uuid}`}
      level={0}
      options={options}
      localization={{
        // @ts-ignore
        ...MTableBody.defaultProps.localization.editRow,
        ...localization.editRow,
        dateTimePickerLocalization: localization.dateTimePickerLocalization,
      }}
      onRowSelected={onRowSelected}
      actions={actions}
      columns={columns}
      getFieldValue={getFieldValue}
      detailPanel={detailPanel}
      path={path}
      onToggleDetailPanel={onToggleDetailPanel}
      onRowClick={onRowClick}
      onRowDoubleClick={onRowDoubleClick}
      isTreeData={isTreeData}
      onTreeExpandChanged={onTreeExpandChanged}
      onEditingCanceled={onEditingCanceled}
      onEditingApproved={onEditingApproved}
      hasAnyEditingRow={hasAnyEditingRow}
      treeDataMaxLevel={treeDataMaxLevel}
      cellEditable={cellEditable}
      onCellEditStarted={onCellEditStarted}
      onCellEditFinished={onCellEditFinished}
      scrollWidth={scrollWidth}
      // custom props
      sx={sx}
    />
  );
};

export default memo(TableRowRenderer);
